import { Component, OnInit, ViewChild } from '@angular/core';
import { LazySelectableTableComponent } from 'src/app/shared/primeng/abstract-classes/lazy-selectable-table.component';
import { DataQuerySearchDto } from '../shared/dto/data-query-search.dto';
import { DataQueryDto } from '../shared/dto/data-query.dto';
import { DatagridService, NotificationService, PagedResultGeneric, TranslationService } from 'src/app/shared';
import { ProfileSearchParameterService } from 'src/app/profiles/shared/profile-search-parameter.service';
import { NavigationService } from 'src/app/shared/navigation/navigation.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ConfirmationService, MenuItem, SelectItem } from 'primeng/api';
import { ProfileSearchComponentModel } from 'src/app/profiles/search/profile-search-component.model';
import { ReportService } from '../shared/report-service';
import { ReportsQueryToolDateParamDialogComponent } from './reports-query-tool-date-param-dialog/reports-query-tool-date-param-dialog.component';
import { ProfileSearchV2ParameterService } from 'src/app/profiles/shared/profile-search-v2-parameter.service';
import { UserService } from 'src/app/users/shared/user.service';
import { UserRead } from 'src/app/users/shared/user-read.model';
import { ConfigServiceService } from 'src/app/config/shared/config-service.service';

@Component({
  selector: 'wl-reports-query-tool-saved-queries',
  templateUrl: './reports-query-tool-saved-queries.component.html',
  styleUrls: ['./reports-query-tool-saved-queries.component.less'],
  providers: [ProfileSearchParameterService],
    standalone: false
})
export class ReportsQueryToolSavedQueriesComponent
  extends LazySelectableTableComponent<DataQuerySearchDto, DataQueryDto>
  implements OnInit
{
  @ViewChild(ReportsQueryToolDateParamDialogComponent) _reportsQueryToolDateParamDialogComponent: ReportsQueryToolDateParamDialogComponent;

  pagingSizeItems: SelectItem[] = [];
  showProfileSelectorModal = false;
  showDatePrompt = false;
  currentQuery = {} as DataQueryDto;
  queryAction: QueryActionEnum;
  rowMenuItems = new Map<number, MenuItem[]>();
  user: UserRead;
  useV2ProfileSelector: boolean = false;
  constructor(
    datagridService: DatagridService,
    private _reportService: ReportService,
    private _navigationService: NavigationService,
    private _notificationService: NotificationService,
    private _profileSearchParameterService: ProfileSearchParameterService,
    private _profileSearchV2ParameterService: ProfileSearchV2ParameterService,
    private _configService: ConfigServiceService,
    private _userService: UserService,
    private _confirmationService: ConfirmationService,
    private _translationService: TranslationService
  ) {
    super('Id', datagridService);
    this.searchParams = { IncludeXml: false, PageSize: 20, OrderBy: 'Name' } as DataQuerySearchDto;
  }

  async ngOnInit(): Promise<void> {
    this._userService.getCurrentUser().then((user) => {
      this.user = user;
      this._configService.checkForProfileSearchConfig(this.user.UserNum.toString()).then((hasProfileSelectorConfig) => {
        this.useV2ProfileSelector = hasProfileSelectorConfig;
      });
    });
  }

  async exportQuery(savedQuery: DataQueryDto) {
    if ((savedQuery.DateParameters?.length ?? 0) > 0) {
      this.currentQuery = savedQuery;
      this.queryAction = QueryActionEnum.Export;
      this._reportsQueryToolDateParamDialogComponent.openDialog(this.currentQuery);
    } else {
      this._reportService
        .exportDataQueryWithId(savedQuery.Id)
        .then((url) => {
          this._navigationService.popupSafeNavigate(url);
        })
        .catch((error: HttpErrorResponse) => {
          if (error.status == 408 || error.status == 503) {
            this._notificationService.translateError('reports.query-tool.view-results.timeout-error');
          } else {
            this._notificationService.httpError(error);
          }
        });
    }
  }

  openInProfileSelector(savedQuery: DataQueryDto) {
    if (!/(profile_id)|(profile id)|(profileid)/i.test(savedQuery.Sql)) {
      const errMsg = this._translationService.getInstant('reports.saved-queries.profile-selector-not-supported');

      this._notificationService.error(errMsg);
      return;
    }

    if ((savedQuery.DateParameters?.length ?? 0) > 0) {
      this.queryAction = QueryActionEnum.ViewInProfileSelector;
      this.currentQuery = savedQuery;
      this._reportsQueryToolDateParamDialogComponent.openDialog(this.currentQuery);
    } else {
      this._reportService
        .getDataQueryProfileIdsWithId(savedQuery.Id)
        .then((data) => {
          const profileids = data;
          const profileSearchModel = new ProfileSearchComponentModel();
          profileSearchModel.searchParams.PermitProfiles = profileids;
          if (profileids && profileids.length) {
            this._profileSearchV2ParameterService.setSearchLoadOptions({ RunOnLoad: true, HideAdvancedParams: true, FromNavigation: true });
            if (this.useV2ProfileSelector) {
              // Having both V2 and original results stored locally will mess up opening the V2 Profile Selector so clear it out.
              this._profileSearchParameterService.clearProfileSearchResults();
              this._profileSearchV2ParameterService.setProfileSearchResults(profileSearchModel);
            } else {
              this._profileSearchParameterService.setProfileSearchResults(profileSearchModel);
            }
            this._navigationService.navigateToProfileFinder();
          } else {
            this._notificationService.translateError('reports.saved-queries.no-results');
          }
        })
        .catch((err: HttpErrorResponse) => {
          this._notificationService.httpError(err);
        });
    }
  }

  pageSizeChanged() {
    this.searchParams.PageNumber = 1;
    this.search();
  }

  fetchResults(): Promise<PagedResultGeneric<DataQueryDto>> {
    return this._reportService.getQueries(this.searchParams);
  }

  openEditDataQuery(savedQuery: DataQueryDto) {
    const command = savedQuery.IsRawSql ? 'sql' : null;
    this._navigationService.navigateToQueryTool(savedQuery.Id, command);
  }

  async acceptDateParams(query: DataQueryDto) {
    this.currentQuery = query;
    this.showDatePrompt = false;
    switch (this.queryAction) {
      case QueryActionEnum.Export:
        this._reportService
          .exportDataQueryWithDates(this.currentQuery)
          .then((url) => {
            this._navigationService.popupSafeNavigate(url);
          })
          .catch((error: HttpErrorResponse) => {
            if (error.status == 408 || error.status == 503) {
              this._notificationService.translateError('reports.query-tool.view-results.timeout-error');
            } else {
              this._notificationService.httpError(error);
            }
          });
        break;
      case QueryActionEnum.ViewInProfileSelector:
        this._reportService
          .getQueryProfileIdsWithDates(this.currentQuery)
          .then((data) => {
            const profileids = data;
            const profileSearchModel = new ProfileSearchComponentModel();
            if (profileids && profileids.length) {
              profileSearchModel.searchParams.PermitProfiles = profileids;
              this._profileSearchV2ParameterService.setSearchLoadOptions({
                RunOnLoad: true,
                HideAdvancedParams: true,
                FromNavigation: true,
              });
              if (this.useV2ProfileSelector) {
                // Having both V2 and original results stored locally will mess up opening the V2 Profile Selector so clear it out.
                this._profileSearchParameterService.clearProfileSearchResults();
                this._profileSearchV2ParameterService.setProfileSearchResults(profileSearchModel);
              } else {
                this._profileSearchParameterService.setProfileSearchResults(profileSearchModel);
              }
              this._navigationService.navigateToProfileFinder();
            } else {
              this._notificationService.translateError('reports.saved-queries.no-results');
            }
          })
          .catch((err: HttpErrorResponse) => {
            this._notificationService.httpError(err);
          });
        break;
    }
  }

  actionItemsForDataQuery(savedQuery: DataQueryDto): MenuItem[] {
    if (!this.rowMenuItems.has(savedQuery.Id)) {
      this.rowMenuItems.set(savedQuery.Id, [
        {
          label: this._translationService.getInstant('reports.saved-queries.open-in-profile-selector'),
          command: () => this.openInProfileSelector(savedQuery),
        },
        {
          label: this._translationService.getInstantTitlecase('reports.saved-queries.export-data'),
          command: () => this.exportQuery(savedQuery),
        },
        {
          label: this._translationService.getInstantTitlecase('reports.saved-queries.run'),
          command: () => this._navigationService.navigateToQueryTool(savedQuery.Id, 'run'),
        },
        {
          label: this._translationService.getInstantTitlecase('common.edit'),
          command: () => this.openEditDataQuery(savedQuery),
        },
        {
          label: this._translationService.getInstantTitlecase('common.delete'),
          command: () => this.openDeleteDataQuery(savedQuery),
        },
      ]);
    }
    return this.rowMenuItems.get(savedQuery.Id);
  }

  openDeleteDataQuery(dataQuery: DataQueryDto): void {
    this._confirmationService.confirm({
      message: this._translationService.getInstant('reports.saved-queries.confirm-delete-message'),
      key: 'deleteDataQueryCD',
      accept: () => {
        this.loading = true;
        this._reportService
          .deleteQuery(dataQuery.Id)
          .then(() => {
            this._notificationService.translateSuccess('reports.saved-queries.delete-success-message');
            this.search();
          })
          .catch((error) => {
            this._notificationService.httpError(error);
          })
          .finally(() => (this.loading = false));
      },
      reject: () => {
        this._confirmationService.close();
      },
    });
  }

  navigateToNewQuery() {
    this._navigationService.navigateToEmptyQueryTool();
  }
}

export enum QueryActionEnum {
  Export = 0,
  ViewInProfileSelector = 1,
}
